import {
  Stack,
  Button,
  Group,
  Alert,
  SegmentedControl,
  Text,
  Autocomplete,
} from '@mantine/core';
import { ResponsiveDrawer, useIsBottomSheet } from '../UI/ResponsiveDrawer';
import { ResponsiveSelect } from '../UI/ResponsiveSelect';
import { IconAlertCircle, IconSalt, IconScale, IconBaguette } from '@tabler/icons-react';
import { DrawerHeader } from '../UI/DrawerHeader';
import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { PantryItem, PantryItemType, PantryCategory } from '../../types/pantry';
import { normalizeWithAliases, createDisplayName, suggestPantryCategory, areItemsSame } from '../../utils/itemNormalization';
import { notifications } from '@mantine/notifications';
import { searchIngredients, getIngredientByName } from '../../data/ingredientDatabase';
import { FractionQuantityInput } from '../UI/FractionQuantityInput';

type PantryItemFormProps = {
  item?: PantryItem; // If provided, we're editing; otherwise creating
  onSave: (item: PantryItem) => void;
  onDuplicateFound?: (existingItem: PantryItem) => void;
  /** Called when the form is finished: after a successful save, a duplicate
   * hand-off, or cancel. The host closes its drawer / pops its view. */
  onDone: () => void;
  // Pre-fill fields when adding from grocery list
  prefillName?: string;
  prefillQuantity?: number;
  prefillUnit?: string;
  prefillCategory?: string;
  prefillType?: PantryItemType;
  /** Re-initialize the form whenever this value changes (e.g. drawer open state) */
  resetSignal?: unknown;
};

type PantryItemDrawerProps = Omit<PantryItemFormProps, 'onDone' | 'resetSignal'> & {
  opened: boolean;
  onClose: () => void;
};

/**
 * The add/edit pantry item form, host-agnostic: PantryItemDrawer wraps it in
 * its own drawer, and ShoppingListDrawer renders it as a pushed view inside
 * the shopping list sheet.
 */
export const PantryItemForm = ({ item, onSave, onDuplicateFound, onDone, prefillName, prefillQuantity, prefillUnit, prefillCategory, prefillType, resetSignal }: PantryItemFormProps) => {
  const isBottomSheet = useIsBottomSheet();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [type, setType] = useState<PantryItemType>('staple');
  const [quantity, setQuantity] = useState<number>(1);
  const [localQuantityInput, setLocalQuantityInput] = useState('');
  const [unit, setUnit] = useState('');
  const [category, setCategory] = useState<PantryCategory | ''>('');

  const categories: { value: PantryCategory; label: string }[] = [
    { value: 'produce', label: 'Produce' },
    { value: 'meat', label: 'Meat' },
    { value: 'dairy', label: 'Dairy' },
    { value: 'pantry_dry_goods', label: 'Pantry & dry goods' },
    { value: 'spices_seasonings', label: 'Spices & seasonings' },
    { value: 'frozen', label: 'Frozen' },
    { value: 'bakery', label: 'Bakery' },
    { value: 'beverages', label: 'Beverages' },
    { value: 'other', label: 'Other' },
  ];

  const units = [
    {
      group: 'Volume',
      items: [
        { value: 'dash', label: 'dash' },
        { value: 'pinch', label: 'pinch' },
        { value: 'teaspoon', label: 'tsp' },
        { value: 'tablespoon', label: 'tbsp' },
        { value: 'fluid ounce', label: 'fl oz' },
        { value: 'cup', label: 'cup' },
        { value: 'pint', label: 'pt' },
        { value: 'quart', label: 'qt' },
        { value: 'gallon', label: 'gal' },
      ]
    },
    {
      group: 'Weight',
      items: [
        { value: 'ounce', label: 'oz' },
        { value: 'pound', label: 'lb' },
      ]
    },
    {
      group: 'Count',
      items: [
        { value: 'each', label: 'each' },
        { value: 'whole', label: 'whole' },
        { value: 'piece', label: 'piece' },
        { value: 'slice', label: 'slice' },
        { value: 'clove', label: 'clove' },
        { value: 'can', label: 'can' },
        { value: 'bottle', label: 'bottle' },
        { value: 'jar', label: 'jar' },
        { value: 'package', label: 'package' },
        { value: 'bunch', label: 'bunch' },
        { value: 'head', label: 'head' },
        { value: 'stick', label: 'stick' },
      ]
    }
  ];

  // Initialize form when item changes
  useEffect(() => {
    if (item) {
      setName(item.displayName); // Use display name as the user-facing name
      setType(item.type);
      setQuantity(item.quantity || 1);
      setUnit(item.unit || '');
      setCategory(item.category || '');
    } else {
      // Reset form for new item or use prefill values
      setName(prefillName || '');
      setType(prefillType || 'staple');
      setQuantity(prefillQuantity || 1);
      setUnit(prefillUnit || '');
      setCategory((prefillCategory as PantryCategory) || '');
    }
    setError(null);
  }, [item, resetSignal, prefillName, prefillQuantity, prefillUnit, prefillCategory, prefillType]);

  // Auto-suggest category and unit when name changes
  useEffect(() => {
    if (name && !item) { // Only for new items
      const suggestedCategory = suggestPantryCategory(name) as PantryCategory;
      setCategory(suggestedCategory);

      // Auto-suggest unit based on item name only if no prefilled unit
      if (!prefillUnit) {
        const suggestedUnit = suggestUnitForItem(name);
        if (suggestedUnit) {
          setUnit(suggestedUnit);
        }
      }
    }
  }, [name, item, prefillUnit]);

  // Function to suggest appropriate unit based on item name
  const suggestUnitForItem = (itemName: string): string => {
    const name = itemName.toLowerCase().trim();
    
    // Volume-based ingredients (liquids, dairy, etc.)
    const volumeIngredients = [
      'milk', 'cream', 'water', 'broth', 'stock', 'juice', 'oil', 'vinegar',
      'wine', 'beer', 'sauce', 'syrup', 'honey', 'vanilla', 'extract',
      'yogurt', 'sour cream', 'buttermilk', 'coconut milk', 'almond milk',
      'heavy cream', 'half and half', 'marinade', 'dressing'
    ];
    
    // Weight-based ingredients (meat, cheese, etc.)
    const weightIngredients = [
      'beef', 'chicken', 'pork', 'turkey', 'fish', 'salmon', 'tuna', 'shrimp', 'tilapia',
      'ground beef', 'ground turkey', 'ground chicken', 'ground pork',
      'bacon', 'ham', 'sausage', 'cheese', 'butter', 'flour', 'sugar',
      'brown sugar', 'powdered sugar', 'nuts', 'almonds', 'walnuts',
      'pecans', 'chocolate', 'cocoa powder', 'pasta', 'rice', 'quinoa',
      'oats', 'beans', 'lentils', 'mushrooms'
    ];
    
    // Count-based ingredients
    const countIngredients = [
      'egg', 'eggs', 'onion', 'onions', 'apple', 'apples', 'banana', 'bananas',
      'orange', 'oranges', 'lemon', 'lemons', 'lime', 'limes', 'potato', 'potatoes',
      'tomato', 'tomatoes', 'bell pepper', 'peppers', 'carrot', 'carrots',
      'clove', 'cloves', 'garlic clove', 'bay leaf', 'bay leaves'
    ];
    
    // Check for spices and seasonings first (most specific)
    if (name.includes('powder') || name.includes('spice') || name.includes('seasoning') ||
        name.includes('salt') || name.includes('pepper') || name.includes('paprika') ||
        name.includes('cumin') || name.includes('oregano') || name.includes('basil') ||
        name.includes('thyme') || name.includes('rosemary') || name.includes('sage') ||
        name.includes('cinnamon') || name.includes('nutmeg') || name.includes('ginger') ||
        name.includes('turmeric') || name.includes('curry') || name.includes('chili') ||
        name.includes('cayenne') || name.includes('garlic powder') || name.includes('onion powder') || 
        name.includes('dried') || name.includes('allspice') || name.includes('cardamom') ||
        name.includes('cloves') || name.includes('coriander') || name.includes('fennel') ||
        name.includes('mustard seed') || name.includes('bay leaf') || name.includes('parsley') ||
        name.includes('cilantro') || name.includes('dill')) {
      return 'teaspoon';
    }
    
    // Check for volume ingredients
    for (const ingredient of volumeIngredients) {
      if (name.includes(ingredient)) {
        return 'cup';
      }
    }
    
    // Check for weight ingredients
    for (const ingredient of weightIngredients) {
      if (name.includes(ingredient)) {
        return name.includes('ground') || name.includes('flour') || name.includes('sugar') 
          ? 'pound' : 'ounce';
      }
    }
    
    // Check for count ingredients
    for (const ingredient of countIngredients) {
      if (name.includes(ingredient)) {
        return name.includes('clove') ? 'clove' : 'each';
      }
    }
    
    if (name.includes('can') || name.includes('jar')) {
      return 'can';
    }
    
    // Default to 'each' for unrecognized ingredients
    return 'each';
  };

  const handleSave = async () => {
    if (!user) return;

    // Validation
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (type === 'ingredient' && (!quantity || !unit)) {
      setError('Quantity and unit are required for ingredient items');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const token = await user.getIdToken();

      // Handle bulk staple creation (comma-separated names)
      if (!item && type === 'staple' && name.includes(',')) {
        const stapleNames = name.split(',').map(n => n.trim()).filter(n => n.length > 0);
        
        if (stapleNames.length === 0) {
          setError('Please enter at least one staple name');
          return;
        }

        const createdItems: PantryItem[] = [];
        const duplicateItems: string[] = [];
        
        for (const stapleName of stapleNames) {
          const payload = {
            name: normalizeWithAliases(stapleName),
            displayName: createDisplayName(stapleName),
            type: 'staple',
            category: suggestPantryCategory(stapleName),
          };

          try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/pantry`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(payload),
            });

            if (response.ok) {
              const data = await response.json();
              createdItems.push(data.item);
            } else {
              const errorData = await response.json();
              if (errorData.error && errorData.error.includes('already exists')) {
                duplicateItems.push(stapleName);
              } else {
                throw new Error(`Failed to create ${stapleName}: ${errorData.error}`);
              }
            }
          } catch (itemError: any) {
            console.error(`Error creating staple ${stapleName}:`, itemError);
            duplicateItems.push(stapleName);
          }
        }

        // Show results
        if (createdItems.length > 0) {
          // For bulk creation, call onSave for each item to update parent state
          createdItems.forEach(item => onSave(item));
          
          let message = `Added ${createdItems.length} staple${createdItems.length > 1 ? 's' : ''} to pantry`;
          if (duplicateItems.length > 0) {
            message += ` (${duplicateItems.length} already existed)`;
          }
          
          notifications.show({
            title: 'Bulk staples added',
            message,
            color: 'green',
            autoClose: 4000,
          });

          onDone();
        } else if (duplicateItems.length > 0) {
          setError(`All items already exist in your pantry: ${duplicateItems.join(', ')}`);
        } else {
          setError('Failed to create any staples');
        }
        return;
      }

      // Single item creation/editing (existing logic)
      const endpoint = item 
        ? `${process.env.REACT_APP_API_URL}/api/pantry/${item.id}`
        : `${process.env.REACT_APP_API_URL}/api/pantry`;
      
      const method = item ? 'PUT' : 'POST';

      const payload = {
        name: normalizeWithAliases(name.trim()),
        displayName: createDisplayName(name.trim()),
        type,
        quantity: type === 'staple' ? undefined : quantity,
        unit: type === 'staple' ? undefined : unit,
        category: category || undefined,
      };

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json();
        
        // Check if this is a duplicate error when creating a new item
        if (!item && data.error && data.error.includes('already exists') && onDuplicateFound) {
          // Try to fetch the existing item and switch to edit mode
          try {
            const existingResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/pantry`, {
              headers: {
                'Authorization': `Bearer ${token}`,
              },
            });
            
            if (existingResponse.ok) {
              const existingData = await existingResponse.json();
              const inputName = name.trim();
              const existingItem = existingData.items.find((i: PantryItem) => 
                areItemsSame(inputName, i.name)
              );
              
              if (existingItem) {
                // Finish here and let the host handle switching to edit
                onDone();
                onDuplicateFound(existingItem);
                setLoading(false);
                return;
              }
            }
          } catch (fetchError) {
            console.error('Error fetching existing item:', fetchError);
          }
        }
        
        throw new Error(data.error || 'Failed to save pantry item');
      }

      const data = await response.json();
      onSave(data.item);
      onDone();
    } catch (error: any) {
      console.error('Error saving pantry item:', error);
      setError(error.message || 'Failed to save pantry item');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setError(null);
    onDone();
  };

  return (
    <Stack gap="md">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error"
            color="red"
            variant="light"
            radius="lg"
          >
            {error}
          </Alert>
        )}

        <Autocomplete
          label="Name"
          placeholder={type === 'staple' ? "e.g. Salt, Pepper, Garlic powder, Oregano" : "e.g. All-purpose flour"}
          value={name}
          onChange={setName}
          onOptionSubmit={(value) => {
            // Auto-populate unit and type when selecting from autocomplete
            const ingredientData = getIngredientByName(value);
            setName(value);
            if (ingredientData) {
              // Set type based on isStaple flag (default to ingredient if not specified)
              if (!item) { // Only change type when adding new items, not editing
                setType(ingredientData.isStaple ? 'staple' : 'ingredient');
              }
              // Set unit if available and not prefilled
              if (ingredientData.commonUnit && !prefillUnit) {
                setUnit(ingredientData.commonUnit);
              }
            }
          }}
          data={name.length >= 2 ? searchIngredients(name, 8).map(ing => ing.displayName ?? ing.name) : []}
          required
          maxLength={type === 'staple' ? 500 : 100}
          limit={8}
          comboboxProps={{ withinPortal: !isBottomSheet }}
        />

        <Stack gap="xs">
          <Text size="sm" fw={500}>
            Type
          </Text>
          <SegmentedControl
            value={type}
            onChange={(value) => setType(value as PantryItemType)}
            data={[
              {
                value: 'staple',
                label: (
                  <Group gap="xs" justify="center">
                    <IconSalt size={16} />
                    <span>Staple</span>
                  </Group>
                ),
              },
              {
                value: 'ingredient',
                label: (
                  <Group gap="xs" justify="center">
                    <IconScale size={16} />
                    <span>Ingredient</span>
                  </Group>
                ),
              },
            ]}
            fullWidth
          />
        </Stack>

        {type === 'ingredient' && (
          <Group grow>
            <FractionQuantityInput
              value={quantity}
              onChange={setQuantity}
              localInput={localQuantityInput}
              onLocalInputChange={setLocalQuantityInput}
              required
            />
            <ResponsiveSelect
              label="Unit"
              value={unit}
              onChange={(value) => setUnit(value || '')}
              data={units}
              placeholder="Select unit"
              required
            />
          </Group>
        )}

        <ResponsiveSelect
          label="Category"
          value={category}
          onChange={(value) => setCategory(value as PantryCategory)}
          data={categories}
          placeholder="Select category"
          clearable
        />

        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={loading}>
            {item ? 'Save changes' : 'Add item'}
          </Button>
        </Group>
    </Stack>
  );
};

export const PantryItemDrawer = ({ opened, onClose, item, ...formProps }: PantryItemDrawerProps) => (
  <ResponsiveDrawer
    opened={opened}
    onClose={onClose}
    title={
      <DrawerHeader
        icon={<IconBaguette size={18} />}
        title={item ? 'Edit pantry item' : 'Add pantry item'}
      />
    }
    size="md"
  >
    <PantryItemForm item={item} onDone={onClose} resetSignal={opened} {...formProps} />
  </ResponsiveDrawer>
);
